import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MdFileDownload, MdPictureAsPdf } from 'react-icons/md';
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { reportesAPI } from '../services/api';

const ReportsContainer = styled.div`
  max-width: 1400px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const ExportButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
`;

const ExportButton = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background-color: ${props => props.primary ? props.theme.colors.primary : props.theme.colors.backgroundLight};
  color: ${props => props.primary ? 'white' : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.primary ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  svg {
    font-size: 1.25rem;
  }

  &:hover {
    background-color: ${props => props.primary ? props.theme.colors.primaryDark : props.theme.colors.background};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.sm};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const StatLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const StatValue = styled.div`
  font-size: ${props => props.theme.fontSizes.xxxl};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.color || props.theme.colors.textPrimary};
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const ChartCard = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

const ChartTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const ChartDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${props => props.theme.spacing.xl} 0;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const COLORS = ['#5B9BD5', '#E57373', '#FFA726', '#66BB6A'];

const Reports = () => {
  const [reportsData, setReportsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReportsData = async () => {
      try {
        setLoading(true);
        const response = await reportesAPI.getDashboardCompleto();
        setReportsData(response.data.data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Error al cargar datos de reportes');
        console.error('Error al cargar reportes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReportsData();
  }, []);

  const exportToCSV = () => {
    if (!reportsData) return;
    
    const csvData = [
      ['Mes', 'Ingresos', 'Pagos Atrasados'],
      ...reportsData.incomeByMonth.map((item, index) => [
        item.month,
        item.amount,
        reportsData.overdueByMonth[index]?.amount || 0
      ])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'reporte_financiero.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    if (!reportsData) return;
    
    const doc = new jsPDF();
    
    // Título
    doc.setFontSize(20);
    doc.text('Reporte Financiero', 14, 20);
    
    // Fecha
    doc.setFontSize(10);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-MX')}`, 14, 30);
    
    // Resumen
    doc.setFontSize(14);
    doc.text('Resumen Financiero', 14, 45);
    
    doc.setFontSize(10);
    doc.text(`Ingresos Totales: $${reportsData.totalIncome.toLocaleString('es-MX')}`, 14, 55);
    doc.text(`Pagos Atrasados: $${reportsData.totalOverdue.toLocaleString('es-MX')}`, 14, 62);
    doc.text(`Promedio Mensual: $${reportsData.monthlyAverage.toLocaleString('es-MX')}`, 14, 69);
    
    // Tabla de datos mensuales
    const tableData = reportsData.incomeByMonth.map((item, index) => [
      item.month,
      `$${item.amount.toLocaleString('es-MX')}`,
      `$${(reportsData.overdueByMonth[index]?.amount || 0).toLocaleString('es-MX')}`
    ]);
    
    doc.autoTable({
      startY: 80,
      head: [['Mes', 'Ingresos', 'Pagos Atrasados']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [91, 155, 213] }
    });
    
    // Top Clientes
    doc.setFontSize(14);
    doc.text('Top 5 Clientes', 14, doc.lastAutoTable.finalY + 15);
    
    const clientsData = reportsData.topClients.map(client => [
      client.name,
      `$${client.amount.toLocaleString('es-MX')}`,
      client.payments.toString()
    ]);
    
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Cliente', 'Monto Total', 'Pagos']],
      body: clientsData,
      theme: 'grid',
      headStyles: { fillColor: [91, 155, 213] }
    });
    
    doc.save('reporte_financiero.pdf');
  };

  if (loading) {
    return (
      <ReportsContainer>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Cargando reportes...</p>
        </div>
      </ReportsContainer>
    );
  }

  if (error) {
    return (
      <ReportsContainer>
        <div style={{ textAlign: 'center', padding: '40px', color: '#E57373' }}>
          <p>Error: {error}</p>
          <ExportButton onClick={() => window.location.reload()}>Reintentar</ExportButton>
        </div>
      </ReportsContainer>
    );
  }

  if (!reportsData) {
    return null;
  }

  return (
    <ReportsContainer>
      <PageHeader>
        <Title>Reportes y Analítica</Title>
        <ExportButtons>
          <ExportButton onClick={exportToCSV}>
            <MdFileDownload />
            Exportar CSV
          </ExportButton>
          <ExportButton primary onClick={exportToPDF}>
            <MdPictureAsPdf />
            Exportar PDF
          </ExportButton>
        </ExportButtons>
      </PageHeader>

      <StatsGrid>
        <StatCard>
          <StatLabel>Ingresos Totales</StatLabel>
          <StatValue color="#5B9BD5">
            ${(reportsData.totalIncome || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
          </StatValue>
        </StatCard>

        <StatCard>
          <StatLabel>Pagos Atrasados</StatLabel>
          <StatValue color="#E57373">
            ${(reportsData.totalOverdue || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
          </StatValue>
        </StatCard>

        <StatCard>
          <StatLabel>Promedio Mensual</StatLabel>
          <StatValue color="#66BB6A">
            ${(reportsData.monthlyAverage || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
          </StatValue>
        </StatCard>
      </StatsGrid>

      <ChartsGrid>
        <ChartCard>
          <ChartTitle>Ingresos Mensuales</ChartTitle>
          <ChartDescription>
            Visualización de los ingresos totales por mes durante el último año
          </ChartDescription>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={reportsData.incomeByMonth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#6C757D" />
              <YAxis stroke="#6C757D" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px'
                }}
                formatter={(value) => `$${value.toLocaleString('es-MX')}`}
              />
              <Legend />
              <Bar dataKey="amount" fill="#5B9BD5" name="Ingresos" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Pagos Atrasados por Mes</ChartTitle>
          <ChartDescription>
            Seguimiento de pagos vencidos y atrasados durante el año
          </ChartDescription>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={reportsData.overdueByMonth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#6C757D" />
              <YAxis stroke="#6C757D" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px'
                }}
                formatter={(value) => `$${value.toLocaleString('es-MX')}`}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="amount" 
                stroke="#E57373" 
                strokeWidth={3}
                name="Pagos Atrasados"
                dot={{ fill: '#E57373', r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </ChartsGrid>

      <TwoColumnGrid>
        <ChartCard>
          <ChartTitle>Métodos de Pago</ChartTitle>
          <ChartDescription>
            Distribución de métodos de pago utilizados
          </ChartDescription>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={reportsData.paymentMethods || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {(reportsData.paymentMethods || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name, props) => [
                  `$${(props.payload.amount || 0).toLocaleString('es-MX')}`,
                  props.payload.name
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Top 5 Clientes</ChartTitle>
          <ChartDescription>
            Clientes con mayores ingresos generados
          </ChartDescription>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={reportsData.topClients || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" stroke="#6C757D" />
              <YAxis dataKey="name" type="category" stroke="#6C757D" width={150} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px'
                }}
                formatter={(value) => `$${value.toLocaleString('es-MX')}`}
              />
              <Bar dataKey="amount" fill="#66BB6A" name="Monto Total" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </TwoColumnGrid>
    </ReportsContainer>
  );
};

export default Reports;
